import { EventProductVariant, Event, PricingCategory } from './event';
import { api, writeDB } from './store';
import { groupBy, uniq } from 'lodash';
import { euroCountryCodes } from '../util/util';

export interface VATRatePerCountry {
  de?: number;
  at: number;
  be: number;
  bg: number;
  hr: number;
  cy: number;
  cz: number;
  dk: number;
  ee: number;
  fi: number;
  fr: number;
  gr: number;
  hu: number;
  ie: number;
  it: number;
  lv: number;
  lt: number;
  lu: number;
  mt: number;
  nl: number;
  pl: number;
  pt: number;
  ro: number;
  sk: number;
  si: number;
  es: number;
  se: number;
  world?: number;
}

export interface FormatedVatRate {
  diverging: Partial<VATRatePerCountry>;
  same: Partial<VATRatePerCountry>;
}

export function pricingCategoryTotal(productVariant: EventProductVariant): number {
  return (
    productVariant.basePrice +
    productVariant.presaleFee +
    productVariant.systemFee +
    productVariant.priceParts.reduce((sum, part) => sum + part.price, 0)
  );
}

export function calculateBasePriceFromTotal(total: number, productVariant: EventProductVariant): number {
  return (
    total -
    (productVariant.presaleFee +
      productVariant.systemFee +
      productVariant.priceParts.reduce((sum, part) => sum + part.price, 0))
  );
}

export function calculatePresaleFee(basePrice: number, productVariant: EventProductVariant): number {
  if (!productVariant.presaleFeePercentage) {
    return productVariant.presaleFee;
  }

  return basePrice * productVariant.presaleFeePercentage;
}

export function applyTax(base: number, taxRate: number): number {
  const result = base * (1 + taxRate);
  return result;
}

export async function loadPricingCategories({ event, local }: { event: Event; local?: boolean }): Promise<void> {
  const pricingCategories = await api.indexPricingCategories({
    eventId: event.id,
    local,
  });

  writeDB.table('events').update(event.id, {
    pricingCategories: pricingCategories.data as PricingCategory[],
  });

  writeDB.table('pricingCategories').upsert(
    pricingCategories.data.map((cat) => {
      return {
        ...cat,
        eventId: event.id,
      };
    })
  );
}

export async function getPricingPartNames({ event }: { event: Event }): Promise<string[]> {
  await loadPricingCategories({ event, local: false });
  return uniq(
    writeDB
      .table('pricingCategories')
      .where({ eventId: event.id })
      .flatMap((category) => category.productVariants.flatMap((pv) => pv.priceParts.map((part) => part.description)))
  );
}

export function fillEUTaxRates(defaultRate?: number): VATRatePerCountry {
  const filledTaxRates = {};
  Object.keys(euroCountryCodes).forEach((code) => {
    if (code !== 'de' && code !== 'world') {
      filledTaxRates[code] = defaultRate;
    }
  });

  return filledTaxRates as VATRatePerCountry;
}

export function hasDivergingValues({
  vatRates,
  scope = 'Global',
}: {
  vatRates: VATRatePerCountry;
  scope?: 'Global' | 'EU';
}): boolean {
  const EURates = { ...vatRates };
  delete EURates.de;
  delete EURates.world;

  const grouppedRates = groupBy(Object.entries(scope === 'Global' ? vatRates : EURates), (value) => value[1]);
  if (Object.keys(grouppedRates).length > 1) {
    return true;
  }
  return false;
}
